import {Logger} from "@AppBuilderLib/shared/lib/logger";
import {
	APP_BUILDER_CUSTOM_EVENT_PATTERN,
	type AppBuilderApplicationEvent,
	type AppBuilderCustomEventName,
	type AppBuilderInteractionEvent,
	type AppBuilderUiEvent,
	type IAppBuilderActionSlot,
	type IAppBuilderActionSlotEventProps,
	type IAppBuilderActionSlotEventPropsSelection,
	type IAppBuilderActionSlots,
} from "../config/appbuilderActionSlots";

/**
 * Runtime allowlists and matching helpers for action slots.
 *
 * JSON validation (Zod) already rejects event names a node cannot emit,
 * invalid `custom:` names, and `eventProps` whose `type` does not match
 * the event. These helpers still select the subset a wrapper runs
 * (viewport vs application) and skip incompatible slots that bypass Zod.
 * UI event names are the keys of {@link APP_BUILDER_UI_EVENT_REACT_PROPS}.
 */

/**
 * DOM event name → React listener prop.
 * {@link APP_BUILDER_UI_EVENTS} is derived from these keys so the two cannot drift.
 */
export const APP_BUILDER_UI_EVENT_REACT_PROPS = {
	click: "onClick",
	contextmenu: "onContextMenu",
	pointerdown: "onPointerDown",
	pointerup: "onPointerUp",
	pointerenter: "onPointerEnter",
	pointerleave: "onPointerLeave",
} as const satisfies Record<AppBuilderUiEvent, string>;

/** Pointer, click, and contextmenu events valid on widgets, tabs, containers, controls, toolbar items, and the viewport host. */
export const APP_BUILDER_UI_EVENTS = Object.keys(
	APP_BUILDER_UI_EVENT_REACT_PROPS,
) as AppBuilderUiEvent[];

/**
 * Viewer interaction slots. Valid on the root `IAppBuilder` only.
 * Viewer `interaction.multiSelect.*` is mapped onto `selecton` / `selectoff`.
 */
export const APP_BUILDER_INTERACTION_EVENTS = [
	"selecton",
	"selectoff",
	"hoveron",
	"hoveroff",
] as const satisfies readonly AppBuilderInteractionEvent[];

/**
 * Map a viewer `EVENTTYPE_INTERACTION` to the JSON slot name.
 * Multi-select on/off runs the same slots as select on/off.
 * `SELECT_OFF` during a reselection is skipped (the following `SELECT_ON`
 * still runs `selecton`). Other interaction types are ignored.
 */
export function mapViewerInteractionEventToSlot(
	viewerEventType: string,
	payload?: {reselection?: boolean},
): AppBuilderInteractionEvent | undefined {
	switch (viewerEventType) {
		case "interaction.select.on":
		case "interaction.multiSelect.on":
			return "selecton";
		case "interaction.select.off":
			if (payload?.reselection) return undefined;
			return "selectoff";
		case "interaction.multiSelect.off":
			return "selectoff";
		case "interaction.hover.on":
			return "hoveron";
		case "interaction.hover.off":
			return "hoveroff";
		default:
			return undefined;
	}
}

/**
 * Application events. `appready`, computation, export, and viewer
 * interaction (`selecton` / `selectoff` / `hoveron` / `hoveroff`) are
 * valid on the root `IAppBuilder` only.
 */
export const APP_BUILDER_APPLICATION_EVENTS: readonly AppBuilderApplicationEvent[] =
	[
		"appready",
		"computationstart",
		"computationend",
		"computationerror",
		"exportstart",
		"exportend",
		"exporterror",
		...APP_BUILDER_INTERACTION_EVENTS,
	];

export function isAppBuilderInteractionEvent(
	eventName: string,
): eventName is AppBuilderInteractionEvent {
	return (APP_BUILDER_INTERACTION_EVENTS as readonly string[]).includes(
		eventName,
	);
}

export function isAppBuilderApplicationEvent(
	eventName: string,
): eventName is AppBuilderApplicationEvent {
	return (APP_BUILDER_APPLICATION_EVENTS as readonly string[]).includes(
		eventName,
	);
}

export function isAppBuilderCustomEvent(
	eventName: string,
): eventName is AppBuilderCustomEventName {
	return APP_BUILDER_CUSTOM_EVENT_PATTERN.test(eventName);
}

const customActionSlotTriggers = new Map<
	string,
	Set<() => void | Promise<void>>
>();

function customActionSlotKey(eventName: string, namespace: string): string {
	return `${namespace}\0${eventName}`;
}

/**
 * Register a root / application `custom:*` trigger for `namespace`.
 * UI nodes (widgets, tabs, containers) use `useDispatchAppBuilderCustomEvent`
 * instead so sibling nodes do not share one bus.
 */
export function registerAppBuilderCustomActionSlot(
	eventName: string,
	trigger: () => void | Promise<void>,
	namespace: string,
): () => void {
	if (!isAppBuilderCustomEvent(eventName)) return () => {};
	const key = customActionSlotKey(eventName, namespace);
	let triggers = customActionSlotTriggers.get(key);
	if (!triggers) {
		triggers = new Set();
		customActionSlotTriggers.set(key, triggers);
	}
	triggers.add(trigger);
	return () => {
		triggers!.delete(trigger);
		if (triggers!.size === 0) customActionSlotTriggers.delete(key);
	};
}

/**
 * Run application / root `custom:*` slots. Pass `namespace` to target one
 * session. Omit it to run every session that registered this name.
 * Widget-level and tab-level slots are not on this bus — call
 * `useDispatchAppBuilderCustomEvent` from the node that owns them.
 */
export function dispatchAppBuilderCustomEvent(
	eventName: string,
	namespace?: string,
): void {
	if (!isAppBuilderCustomEvent(eventName)) {
		Logger.warn(
			`"${eventName}" is not a custom action slot event (expected custom:kebab-case).`,
		);
		return;
	}
	const triggers = new Set<() => void | Promise<void>>();
	if (namespace !== undefined) {
		const set = customActionSlotTriggers.get(
			customActionSlotKey(eventName, namespace),
		);
		set?.forEach((trigger) => triggers.add(trigger));
	} else {
		const suffix = `\0${eventName}`;
		for (const [key, set] of customActionSlotTriggers) {
			if (key.endsWith(suffix)) {
				set.forEach((trigger) => triggers.add(trigger));
			}
		}
	}
	if (triggers.size === 0) {
		Logger.warn(
			`Custom action slot "${eventName}" has no registered listener.`,
		);
		return;
	}
	for (const trigger of triggers) {
		void Promise.resolve(trigger()).catch((error) => {
			Logger.warn("Action slot failed:", error);
		});
	}
}

/**
 * Per-event `eventProps.type`. Events not listed here do not take
 * `eventProps`; a value on those names is incompatible and the slot is skipped.
 */
export const ACTION_SLOT_EVENT_PROPS_TYPE = {
	computationstart: "session",
	computationend: "session",
	computationerror: "session",
	exportstart: "export",
	exportend: "export",
	exporterror: "export",
	selecton: "selection",
	selectoff: "selection",
	hoveron: "selection",
	hoveroff: "selection",
} as const satisfies Partial<
	Record<AppBuilderApplicationEvent, IAppBuilderActionSlotEventProps["type"]>
>;

export function expectedActionSlotEventPropsType(
	eventName: string,
): IAppBuilderActionSlotEventProps["type"] | undefined {
	return ACTION_SLOT_EVENT_PROPS_TYPE[
		eventName as keyof typeof ACTION_SLOT_EVENT_PROPS_TYPE
	];
}

function readSlotEventProps(
	slot: IAppBuilderActionSlot,
): IAppBuilderActionSlotEventProps | undefined {
	if (!("eventProps" in slot) || !slot.eventProps) return undefined;
	return slot.eventProps;
}

/**
 * `eventProps` omitted is valid except on selection events, which require a
 * non-empty `nameFilter`. Wrong `type` for this event, or `eventProps` on an
 * event that has none, is incompatible.
 */
export function isActionSlotEventPropsCompatible(
	eventName: string,
	slot: IAppBuilderActionSlot,
): boolean {
	const eventProps = readSlotEventProps(slot);
	const expected = expectedActionSlotEventPropsType(eventName);
	if (!eventProps) return expected !== "selection";
	if (expected === undefined || eventProps.type !== expected) return false;
	if (eventProps.type === "selection") {
		const {nameFilter} = eventProps.props;
		return Array.isArray(nameFilter) && nameFilter.length > 0;
	}
	return true;
}

function warnIncompatibleActionSlotEventProps(
	eventName: string,
	slot: IAppBuilderActionSlot,
): void {
	const expected = expectedActionSlotEventPropsType(eventName);
	const eventProps = readSlotEventProps(slot);
	if (expected === undefined) {
		Logger.warn(
			`Action slot "${eventName}" does not support eventProps and will be ignored.`,
		);
		return;
	}
	if (!eventProps || eventProps.type !== expected) {
		Logger.warn(
			`Action slot "${eventName}" expects eventProps.type "${expected}" but got "${eventProps?.type}" and will be ignored.`,
		);
		return;
	}
	Logger.warn(
		`Action slot "${eventName}" requires a non-empty eventProps.props.nameFilter and will be ignored.`,
	);
}

/** Viewer `TASK_TYPE.SESSION_CUSTOMIZATION` (string to avoid importing the viewer here). */
export const SESSION_CUSTOMIZATION_TASK_TYPE = "session_customization";

/**
 * `ITaskEvent.status` on `TASK_CANCEL` when a session customize actually
 * failed. Other cancels (superseded customizes) use a different status.
 */
export const SESSION_CUSTOMIZATION_FAILED_STATUS =
	"Session customization failed";

export function isSessionCustomizationFailedTask(event: {
	type?: string;
	status?: string;
}): boolean {
	return (
		event.type === SESSION_CUSTOMIZATION_TASK_TYPE &&
		event.status === SESSION_CUSTOMIZATION_FAILED_STATUS
	);
}

/**
 * Events valid on the App Builder root: application events plus viewport
 * pointer/`click` (those attach to the viewport host, not to a UI node).
 */
export const APP_BUILDER_ROOT_EVENTS: readonly string[] = [
	...APP_BUILDER_APPLICATION_EVENTS,
	...APP_BUILDER_UI_EVENTS,
];

/**
 * Per-node event allowlists. UI kinds currently share {@link APP_BUILDER_UI_EVENTS};
 * change one key here when that node should accept extra (or fewer) names.
 * `root` is the ignored-slot log for the App Builder definition (application + viewport UI).
 */
export const APP_BUILDER_SLOT_EVENTS = {
	widget: APP_BUILDER_UI_EVENTS,
	tab: APP_BUILDER_UI_EVENTS,
	container: APP_BUILDER_UI_EVENTS,
	control: APP_BUILDER_UI_EVENTS,
	toolbar: APP_BUILDER_UI_EVENTS,
	viewport: APP_BUILDER_UI_EVENTS,
	application: APP_BUILDER_APPLICATION_EVENTS,
	root: APP_BUILDER_ROOT_EVENTS,
} as const;

/** Trigger map keyed by UI event name. Written by slot runners, read by DOM handlers. */
export type AppBuilderUiSlotHandlers = {
	[K in AppBuilderUiEvent]?: () => void;
};

/** React pointer/`click`/`contextmenu` props produced by {@link uiSlotDomProps}. */
export type AppBuilderUiSlotDomProps = {
	[K in (typeof APP_BUILDER_UI_EVENT_REACT_PROPS)[AppBuilderUiEvent]]?: (event?: {
		preventDefault(): void;
	}) => void;
};

/**
 * Build React listener props for the UI events in `enabledEvents`.
 *
 * Used by the default wrap in `AppBuilderActionSlots` and by tab controls,
 * which cannot wrap `Tabs.Tab` (Mantine requires it as a direct `Tabs.List` child).
 * `contextmenu` calls `preventDefault` so the browser menu does not also open.
 */
export function uiSlotDomProps(
	run: (eventName: AppBuilderUiEvent) => void,
	enabledEvents: ReadonlySet<string> = new Set(APP_BUILDER_UI_EVENTS),
): AppBuilderUiSlotDomProps {
	const props: AppBuilderUiSlotDomProps = {};
	for (const eventName of APP_BUILDER_UI_EVENTS) {
		if (!enabledEvents.has(eventName)) continue;
		props[APP_BUILDER_UI_EVENT_REACT_PROPS[eventName]] = (event) => {
			if (eventName === "contextmenu") event?.preventDefault();
			run(eventName);
		};
	}
	return props;
}

/**
 * Read `eventProps.props` when the slot filter is of `expectedType`.
 * Returns `undefined` when omitted. Incompatible types are skipped before
 * this is called ({@link pickAllowedActionSlots}).
 */
export function getActionSlotEventProps(
	slot: IAppBuilderActionSlot,
	expectedType: IAppBuilderActionSlotEventProps["type"],
): IAppBuilderActionSlotEventProps["props"] | undefined {
	const eventProps = readSlotEventProps(slot);
	if (!eventProps) return undefined;
	if (eventProps.type !== expectedType) return undefined;
	return eventProps.props;
}

/**
 * Whether a session-scoped event belongs to this slot.
 * `filterSessionId` wins when set; otherwise the controller session is the default.
 * An event with no session id only matches when the slot also has no filter.
 */
export function matchesSessionFilter(
	sessionId: string | undefined,
	filterSessionId: string | undefined,
	fallbackSessionId: string,
): boolean {
	const expected = filterSessionId || fallbackSessionId;
	if (!sessionId) return !filterSessionId;
	return sessionId === expected;
}

/**
 * Whether an export event matches `filterName` (id, name, or displayname, case-insensitive).
 * No filter → all exports of the session.
 */
export function matchesExportName(
	exportIdentity: {
		id?: string;
		name?: string;
		displayname?: string;
	},
	filterName?: string,
): boolean {
	if (!filterName) return true;
	const needle = filterName.toLowerCase();
	return [exportIdentity.id, exportIdentity.name, exportIdentity.displayname]
		.filter((value): value is string => !!value)
		.some((value) => value.toLowerCase() === needle);
}

export type ResolvedActionSlot = {
	eventName: string;
	slot: IAppBuilderActionSlot;
	index: number;
};

/** Flatten one slot or an array of slots. Empty arrays are no slots. */
export function listActionSlots(
	value: IAppBuilderActionSlot | IAppBuilderActionSlot[] | undefined,
): IAppBuilderActionSlot[] {
	if (!value) return [];
	return Array.isArray(value) ? value.filter(Boolean) : [value];
}

/** Handler map key when several slots share an event name. */
export function actionSlotHandlerKey(eventName: string, index: number): string {
	return `${eventName}#${index}`;
}

/**
 * Run matching `custom:*` triggers on this node's handler map.
 * Returns whether any slot ran. Does not warn (callers bubble or warn).
 */
export function runResolvedCustomSlots(
	handlers: Record<string, (() => void) | undefined>,
	resolved: readonly ResolvedActionSlot[],
	eventName: string,
): boolean {
	if (!isAppBuilderCustomEvent(eventName)) return false;
	let ran = false;
	for (const item of resolved) {
		if (item.eventName !== eventName) continue;
		handlers[actionSlotHandlerKey(item.eventName, item.index)]?.();
		ran = true;
	}
	return ran;
}

/**
 * Slots whose event names are in `allowedEvents`, plus valid `custom:*`
 * names when `includeCustomEvents` is true (default). The viewport wrap of
 * root slots passes `includeCustomEvents: false` so root `custom:*` is
 * registered once by application listeners.
 * Arrays on one event name become several {@link ResolvedActionSlot}s.
 * Other names are omitted (see {@link logIgnoredActionSlotEvents}).
 */
export function pickAllowedActionSlots(
	actionSlots: IAppBuilderActionSlots | undefined,
	allowedEvents: readonly string[],
	options?: {includeCustomEvents?: boolean},
): ResolvedActionSlot[] {
	if (!actionSlots) return [];
	const includeCustomEvents = options?.includeCustomEvents !== false;
	const allowed = new Set(allowedEvents);
	const resolved: ResolvedActionSlot[] = [];
	for (const eventName of Object.keys(actionSlots)) {
		if (
			!allowed.has(eventName) &&
			!(includeCustomEvents && isAppBuilderCustomEvent(eventName))
		) {
			continue;
		}
		const listed = listActionSlots(
			(
				actionSlots as Record<
					string,
					IAppBuilderActionSlot | IAppBuilderActionSlot[] | undefined
				>
			)[eventName],
		);
		for (const slot of listed) {
			if (!isActionSlotEventPropsCompatible(eventName, slot)) {
				warnIncompatibleActionSlotEventProps(eventName, slot);
				continue;
			}
			resolved.push({eventName, slot, index: resolved.length});
		}
	}
	return resolved;
}

/**
 * Warn for slots that this node will not run. Valid `custom:*` names are
 * picked by {@link pickAllowedActionSlots} and dispatched via
 * {@link dispatchAppBuilderCustomEvent}.
 */
export function logIgnoredActionSlotEvents(
	actionSlots: IAppBuilderActionSlots | undefined,
	allowedEvents: readonly string[],
	unsupportedOn: string,
): void {
	if (!actionSlots) return;
	const allowed = new Set(allowedEvents);
	for (const eventName of Object.keys(actionSlots)) {
		const listed = listActionSlots(
			(
				actionSlots as Record<
					string,
					IAppBuilderActionSlot | IAppBuilderActionSlot[] | undefined
				>
			)[eventName],
		);
		if (
			listed.length === 0 ||
			allowed.has(eventName) ||
			isAppBuilderCustomEvent(eventName)
		) {
			continue;
		}
		if (eventName.startsWith("custom:")) {
			Logger.warn(
				`Custom action slot "${eventName}" is not a valid custom event name (expected custom:kebab-case) ${unsupportedOn} and will be ignored.`,
			);
		} else {
			Logger.warn(
				`Action slot "${eventName}" is not supported ${unsupportedOn} and will be ignored.`,
			);
		}
	}
}

/**
 * First non-empty string at `keys` on `value`, including one level of nested
 * `{ id | name | sessionId }`. Used to read session id from viewer task `data`
 * without treating a task uuid `id` as a session id.
 */
export function readStringField(
	value: unknown,
	keys: readonly string[],
): string | undefined {
	if (!value || typeof value !== "object") return undefined;
	const record = value as Record<string, unknown>;
	for (const key of keys) {
		const field = record[key];
		if (typeof field === "string" && field.length > 0) return field;
		if (field && typeof field === "object") {
			const nested = readStringField(field, ["id", "name", "sessionId"]);
			if (nested) return nested;
		}
	}
	return undefined;
}

function normalizedNameFilter(
	eventProps: Partial<IAppBuilderActionSlotEventPropsSelection> | undefined,
): string[] | undefined {
	const nameFilter = eventProps?.nameFilter;
	return nameFilter?.length ? nameFilter : undefined;
}

/** Viewport + nameFilter only — used to detect conflicting selection configs. */
export function selectionSlotNameFilterKey(
	eventProps: Partial<IAppBuilderActionSlotEventPropsSelection> | undefined,
	viewportId: string,
): string {
	return JSON.stringify({
		viewportId: eventProps?.viewportId ?? viewportId,
		nameFilter: normalizedNameFilter(eventProps) ?? null,
	});
}

/**
 * Full effective `useSelection` config. Slots that share this key share one
 * interaction manager; different colors/max/hover become separate groups.
 * Color keys preserve omitted vs explicit `null` (`JSON.stringify` drops
 * `undefined`; `null` disables the default effect).
 */
export function selectionSlotGroupKey(
	eventProps: Partial<IAppBuilderActionSlotEventPropsSelection> | undefined,
	viewportId: string,
): string {
	return JSON.stringify({
		viewportId: eventProps?.viewportId ?? viewportId,
		nameFilter: normalizedNameFilter(eventProps) ?? null,
		minimumSelection: eventProps?.minimumSelection ?? 0,
		maximumSelection: eventProps?.maximumSelection ?? 1,
		hover: eventProps?.hover !== false,
		hoverColor: eventProps?.hoverColor,
		selectionColor: eventProps?.selectionColor,
		availableColor: eventProps?.availableColor,
		occludeBySceneGeometry: eventProps?.occludeBySceneGeometry,
	});
}
